import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Phone, X } from 'lucide-react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { telephonyApi, type PhoneContact } from '../../services/telephony'

interface Props {
  channelId: string
  onClose: () => void
}

export default function PhoneDialModal({ channelId, onClose }: Props) {
  const [number, setNumber] = useState('')
  const [recent, setRecent] = useState<PhoneContact[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    telephonyApi.contacts.listForChannel(channelId).then((rows) => {
      if (!cancelled) setRecent(rows.slice(0, 5))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [channelId])

  const parsed = parsePhoneNumberFromString(number)
  const valid = !!(parsed && parsed.isValid())

  const dial = async () => {
    if (!valid) return
    setBusy(true); setError(null)
    try {
      await telephonyApi.channels.inviteByPhone(channelId, number)
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'DIAL_FAILED')
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="w-96 max-w-[90vw] rounded-xl bg-card border border-border p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-foreground">Invite by phone</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition">
            <X size={18} />
          </button>
        </div>
        <input
          autoFocus
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && valid && !busy) dial() }}
          placeholder="+14155550100"
          className="w-full rounded-md bg-muted text-foreground text-sm px-3 py-2 border border-border focus:border-primary outline-none"
        />
        {recent.length > 0 && (
          <div className="mt-3">
            <div className="text-xs text-muted-foreground mb-1">Recent</div>
            <div className="space-y-0.5">
              {recent.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setNumber(c.phoneNumber)}
                  className="block w-full text-left text-sm text-foreground hover:bg-muted rounded px-2 py-1 transition"
                >
                  {c.displayName ? `${c.displayName} — ` : ''}{c.phoneNumber}
                </button>
              ))}
            </div>
          </div>
        )}
        {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
        <button
          disabled={!valid || busy}
          onClick={dial}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground text-sm px-3 py-2 hover:bg-primary/90 transition disabled:opacity-50"
        >
          <Phone size={16} /> {busy ? 'Dialing…' : 'Dial'}
        </button>
      </div>
    </div>,
    document.body,
  )
}
